import { useCallback, useEffect, useState } from 'react'
import { KeyRound, Copy, Trash2 } from 'lucide-react'
import Modal from './ui/Modal'
import ConfirmDialog from './ui/ConfirmDialog'
import { useToast } from './ui/Toast'
import {
  createAccountApiKey,
  createProjectApiKey,
  listAccountApiKeys,
  listProjectApiKeys,
  revokeAccountApiKey,
  revokeProjectApiKey,
  type ProjectApiKey,
} from '../services/rooms'

interface ApiKeysModalProps {
  open: boolean
  /** Sem roomId: chaves da conta (todos os projetos do usuário). */
  roomId?: string
  onClose: () => void
}

function mcpUrl(): string {
  const base = import.meta.env.VITE_API_URL || window.location.origin
  return `${base.replace(/\/$/, '')}/api/v1/mcp`
}

export default function ApiKeysModal({ open, roomId, onClose }: ApiKeysModalProps) {
  const account = !roomId
  const { toast } = useToast()
  const [keys, setKeys] = useState<ProjectApiKey[]>([])
  const [loading, setLoading] = useState(false)
  const [name, setName] = useState('')
  const [canWrite, setCanWrite] = useState(false)
  const [creating, setCreating] = useState(false)
  const [freshKey, setFreshKey] = useState<string | null>(null)
  const [revokeId, setRevokeId] = useState<string | null>(null)
  const [revoking, setRevoking] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      setKeys(roomId ? await listProjectApiKeys(roomId) : await listAccountApiKeys())
    } catch {
      toast({ variant: 'error', title: 'Não foi possível carregar as chaves' })
    } finally {
      setLoading(false)
    }
  }, [roomId, toast])

  useEffect(() => {
    if (open) {
      setFreshKey(null)
      setName('')
      setCanWrite(false)
      void load()
    }
  }, [open, load])

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!name.trim()) return
    setCreating(true)
    try {
      const created = roomId ? await createProjectApiKey(roomId, name.trim(), canWrite) : await createAccountApiKey(name.trim(), canWrite)
      setFreshKey(created.key)
      setName('')
      setCanWrite(false)
      await load()
    } catch (err: unknown) {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail
      toast({ variant: 'error', title: 'Não foi possível criar a chave', description: detail })
    } finally {
      setCreating(false)
    }
  }

  const copy = async (value: string) => {
    try {
      await navigator.clipboard.writeText(value)
      toast({ variant: 'success', title: 'Copiado' })
    } catch {
      toast({ variant: 'error', title: 'Não foi possível copiar' })
    }
  }

  const confirmRevoke = async () => {
    if (!revokeId) return
    setRevoking(true)
    try {
      if (roomId) await revokeProjectApiKey(roomId, revokeId)
      else await revokeAccountApiKey(revokeId)
      setRevokeId(null)
      await load()
    } catch {
      toast({ variant: 'error', title: 'Não foi possível revogar a chave' })
    } finally {
      setRevoking(false)
    }
  }

  return (
    <>
      <Modal
        open={open}
        onClose={onClose}
        title={account ? 'Chaves de API da conta' : 'Chaves de API do projeto'}
        titleAccessory={<KeyRound size={16} className="text-muted" />}
        size="md"
      >
        <p className="text-sm text-muted m-0">
          {account
            ? 'Uma chave da conta acessa todos os seus projetos pelo servidor MCP (Claude, Cursor e outros agentes), com as permissões que você tem em cada um.'
            : 'Cada chave acessa só este projeto, pelo servidor MCP.'}{' '}
          Documentação em <a href="/docs/mcp" target="_blank" rel="noreferrer" className="text-accent">/docs/mcp</a>.
        </p>
        <form onSubmit={handleCreate} className="mt-4 flex flex-wrap gap-2">
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Nome da chave, ex.: Cursor do Carlos"
            aria-label="Nome da chave"
            maxLength={80}
            className="flex-1 h-9 px-3 rounded-lg border border-line bg-canvas text-sm outline-none focus:ring-2 focus:ring-accent"
          />
          <button type="submit" disabled={creating || !name.trim()} className="h-9 px-3 rounded-lg bg-ink text-surface text-sm font-semibold cursor-pointer disabled:opacity-50">
            {creating ? 'Criando...' : 'Criar'}
          </button>
          <label className="basis-full flex items-center gap-2 text-xs text-muted cursor-pointer">
            <input type="checkbox" checked={canWrite} onChange={(e) => setCanWrite(e.target.checked)} className="accent-[var(--af-acc)]" />
            {account
              ? 'Permitir alterações (diagramas do Studio, páginas e diagramas Mermaid) nos projetos em que você é editor ou dono'
              : 'Permitir alterações neste projeto (diagramas do Studio, páginas e diagramas Mermaid)'}
          </label>
        </form>
        {freshKey && (
          <div className="mt-3 p-3 rounded-lg border border-warn-line bg-warn-soft">
            <p className="text-xs font-semibold m-0">Copie agora. Esta chave não será mostrada de novo.</p>
            <div className="mt-2 flex gap-2">
              <input readOnly value={freshKey} aria-label="Chave criada" className="flex-1 h-9 px-2 rounded-md border border-line bg-surface font-mono text-xs" />
              <button type="button" onClick={() => copy(freshKey)} className="h-9 px-2.5 rounded-md border border-line bg-surface cursor-pointer" aria-label="Copiar chave">
                <Copy size={14} />
              </button>
            </div>
            <p className="text-xs text-muted mt-3 mb-1">Para usar no Claude Code:</p>
            <div className="flex gap-2">
              <code className="flex-1 min-w-0 px-2 py-1.5 rounded-md border border-line bg-surface font-mono text-[11px] break-all">
                {`claude mcp add --transport http archforge ${mcpUrl()} --header "Authorization: Bearer ${freshKey}"`}
              </code>
              <button
                type="button"
                onClick={() => copy(`claude mcp add --transport http archforge ${mcpUrl()} --header "Authorization: Bearer ${freshKey}"`)}
                className="h-8 px-2.5 self-start rounded-md border border-line bg-surface cursor-pointer"
                aria-label="Copiar comando"
              >
                <Copy size={14} />
              </button>
            </div>
          </div>
        )}
        <ul className="mt-4 flex flex-col gap-2 m-0 p-0 list-none">
          {loading && <li className="text-sm text-muted">Carregando...</li>}
          {!loading && keys.length === 0 && <li className="text-sm text-muted">Nenhuma chave ativa.</li>}
          {keys.map((key) => (
            <li key={key.id} className="flex items-center gap-3 px-3 py-2 rounded-lg border border-line">
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium truncate flex items-center gap-1.5">
                  {key.name}
                  {key.can_write && <span className="text-[10px] font-semibold uppercase tracking-wide px-1.5 rounded bg-accent-soft text-accent">escrita</span>}
                </div>
                <div className="font-mono text-[11px] text-faint">{key.prefix}…</div>
              </div>
              <button type="button" onClick={() => setRevokeId(key.id)} className="text-muted cursor-pointer" aria-label={`Revogar ${key.name}`}>
                <Trash2 size={14} />
              </button>
            </li>
          ))}
        </ul>
      </Modal>
      <ConfirmDialog
        open={Boolean(revokeId)}
        title="Revogar chave"
        variant="danger"
        confirmLabel="Revogar"
        loading={revoking}
        onCancel={() => setRevokeId(null)}
        onConfirm={confirmRevoke}
      >
        {account ? 'Agentes que usam esta chave perdem o acesso aos seus projetos na hora.' : 'Agentes que usam esta chave perdem o acesso ao projeto na hora.'}
      </ConfirmDialog>
    </>
  )
}
